import React from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Eye, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

export const ProductCard = ({ product, variant = 'default' }) => {
  const { t } = useLanguage();
  const { addItem } = useCart();

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (product.stock > 0) {
      addItem(product);
      toast.success(t('addToCart'), {
        description: product.title
      });
    }
  };

  const placeholderImage = 'https://images.pexels.com/photos/5632399/pexels-photo-5632399.jpeg?w=400&h=400&fit=crop';

  // Luxury variant - Farfetch/Yoox style
  if (variant === 'luxury') {
    return (
      <Link
        to={`/products/${product.id}`}
        className="group block"
        data-testid={`product-card-${product.id}`}
      >
        {/* Image */}
        <div className="relative aspect-[3/4] overflow-hidden bg-neutral-100 mb-4">
          <img
            src={product.images?.[0] || placeholderImage}
            alt={product.title}
            className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700 ease-out"
            onError={(e) => { e.target.src = placeholderImage; }}
          />
          
          {/* Quick View on Hover */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors duration-300" />
        </div>

        {/* Info - Minimal */}
        <div className="space-y-2">
          <p className="text-[10px] text-neutral-400 uppercase tracking-[0.2em]">
            {product.category}
          </p>
          <h3 className="text-sm font-light text-neutral-800 line-clamp-2 leading-relaxed">
            {product.title}
          </h3>
          <div className="flex items-center gap-3">
            <span className="text-sm text-neutral-900">
              €{product.price?.toFixed(2)}
            </span>
            {product.original_price && product.original_price > product.price && (
              <span className="text-xs text-neutral-400 line-through">
                €{product.original_price?.toFixed(2)}
              </span>
            )}
          </div>
        </div>
      </Link>
    );
  }

  // Default variant
  return (
    <Link
      to={`/products/${product.id}`}
      className="group product-card bg-white rounded-2xl overflow-hidden border border-border/50"
      data-testid={`product-card-${product.id}`}
    >
      {/* Image */}
      <div className="relative aspect-square overflow-hidden bg-muted">
        <img
          src={product.images?.[0] || placeholderImage}
          alt={product.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          onError={(e) => { e.target.src = placeholderImage; }}
        />
        {product.is_featured && (
          <span className="absolute top-4 left-4 bg-accent text-accent-foreground text-xs font-bold px-3 py-1 rounded-full">
            Featured
          </span>
        )}
        {product.stock === 0 && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <span className="text-white font-bold">{t('outOfStock')}</span>
          </div>
        )}
        
        {/* Quick Actions */}
        <div className="absolute bottom-4 left-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <Button
            variant="secondary"
            size="sm"
            className="flex-1 bg-white/90 hover:bg-white"
            onClick={handleAddToCart}
            disabled={product.stock === 0}
            data-testid={`add-to-cart-${product.id}`}
          >
            <ShoppingCart className="h-4 w-4 mr-2" />
            {t('addToCart')}
          </Button>
          <Button variant="secondary" size="icon" className="bg-white/90 hover:bg-white shrink-0">
            <Eye className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Info */}
      <div className="p-5">
        <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">
          {product.category}
        </p>
        <h3 className="font-medium text-foreground mb-2 line-clamp-2 min-h-[3rem]">
          {product.title}
        </h3>
        <div className="flex items-center justify-between">
          <span className="text-xl font-bold text-primary">
            €{product.price?.toFixed(2)}
          </span>
          {product.stock > 0 && product.stock <= 5 && (
            <span className="text-xs text-accent font-medium">
              Solo {product.stock} rimasti
            </span>
          )}
        </div>
      </div>
    </Link>
  );
};

export default ProductCard;
